import type { useTranslation } from 'react-i18next';
import type { RevenueComparison } from '@magimp/shared';
import type { useLocale } from '../../../i18n/useLocale.js';
import { formatNumber, formatRub, formatRubCompact } from '../../../i18n/formatters.js';

type T = ReturnType<typeof useTranslation<['revenue', 'common']>>['t'];

interface ComparisonCardProps {
  title: string;
  comp: RevenueComparison;
  locale: ReturnType<typeof useLocale>['locale'];
  t: T;
}

export function ComparisonCard({ title, comp, locale, t }: ComparisonCardProps) {
  if (!comp.available) {
    return (
      <div
        style={{
          border: '1px solid var(--border-1)',
          borderRadius: 'var(--r-2)',
          padding: 12,
          background: 'var(--bg-1)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 6 }}>
          {comp.reason === 'sheet_missing'
            ? t('revenue:comparison.sheetMissing')
            : t('revenue:comparison.noData')}
        </div>
      </div>
    );
  }
  const delta = comp.selectedTotal - comp.comparisonTotal;
  const ratio = comp.comparisonTotal > 0 ? delta / comp.comparisonTotal : null;
  const categoryRows: Array<{
    id: 'buyouts' | 'foreignTrade' | 'logistics';
    selected: number;
    comparison: number;
  }> = [
    {
      id: 'buyouts',
      selected: comp.selectedCategories.buyouts,
      comparison: comp.comparisonCategories.buyouts,
    },
    {
      id: 'foreignTrade',
      selected: comp.selectedCategories.foreignTrade,
      comparison: comp.comparisonCategories.foreignTrade,
    },
    {
      id: 'logistics',
      selected: comp.selectedCategories.logistics,
      comparison: comp.comparisonCategories.logistics,
    },
  ];
  return (
    <div
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 12,
        background: 'var(--bg-1)',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--fg-2)', marginTop: 2 }}>{comp.month.label}</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 8,
          marginTop: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('revenue:comparison.selectedTotal')}
          </div>
          <div
            style={{ fontSize: 16, fontWeight: 600 }}
            title={formatRub(comp.selectedTotal, locale)}
          >
            {formatRubCompact(comp.selectedTotal, locale)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('revenue:comparison.comparisonTotal')}
          </div>
          <div
            style={{ fontSize: 16, fontWeight: 600 }}
            title={formatRub(comp.comparisonTotal, locale)}
          >
            {formatRubCompact(comp.comparisonTotal, locale)}
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 13,
          color: delta >= 0 ? 'var(--status-success-fg)' : 'var(--status-error-fg)',
        }}
      >
        {delta >= 0 ? '+' : '−'}
        {formatRubCompact(Math.abs(delta), locale)}
        {ratio == null ? null : (
          <span style={{ marginLeft: 6, color: 'var(--fg-2)' }}>
            ({formatNumber(Math.round(Math.abs(ratio) * 100), locale)}%)
          </span>
        )}
      </div>
      {/* #602 line 226: comparison scope includes the three categories. */}
      <div
        style={{
          marginTop: 10,
          paddingTop: 8,
          borderTop: '1px solid var(--border-1)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 12,
        }}
      >
        {categoryRows.map((row) => {
          const d = row.selected - row.comparison;
          return (
            <div
              key={row.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
                gap: 6,
                alignItems: 'baseline',
              }}
            >
              <span style={{ color: 'var(--fg-2)' }}>{t(`revenue:kpi.${row.id}`)}</span>
              <span title={formatRub(row.selected, locale)}>
                {formatRubCompact(row.selected, locale)}
              </span>
              <span style={{ color: 'var(--fg-3)' }} title={formatRub(row.comparison, locale)}>
                {formatRubCompact(row.comparison, locale)}
              </span>
              <span
                style={{
                  color: d >= 0 ? 'var(--status-success-fg)' : 'var(--status-error-fg)',
                  minWidth: 56,
                  textAlign: 'right',
                }}
              >
                {d >= 0 ? '+' : '−'}
                {formatRubCompact(Math.abs(d), locale)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
